/**
 * Раздел «Качество и ОТ» (поток 7): замечания и предписания качества и ОТ — тот же единый реестр, что у надзора
 * (раздел 6.10), приёмки по чек-листам (КЧ-04, КЧ-14) и шаблоны чек-листов компании.
 */
import {
  CHECKLIST_STATUSES,
  CHECKLIST_WORK_KINDS,
  type ChecklistInspectionDetail,
  type ChecklistInspectionSummary,
  type ChecklistTemplateSummary,
  type RemarkSource,
} from '@epc/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { Plus } from 'lucide-react';
import { useCallback, useState } from 'react';
import { useCrumbs } from '../../layout/crumbs';
import { api } from '../../lib/api';
import { date, todayLocal } from '../../lib/format';
import { counterpartiesQuery, projectQuery } from '../../lib/projects';
import { useCompanyWide, useMe } from '../../lib/session';
import { Button } from '../../ui/button';
import { Dialog } from '../../ui/dialog';
import { Badge, EmptyState, ErrorBox, Spinner, toast } from '../../ui/feedback';
import { Field, Input, Select } from '../../ui/form';
import { Page, PageHeader } from '../../ui/layout';
import { ProjectFilter, Tabs, useProjectDefault, useProjectsWith } from '../psd/shared';
import { NewRemarkDialog, RemarksList } from '../supervision/SupervisionPage';
import { BriefingsList, CredentialsList, ObservationsList, PermitsList } from './SafetyLists';

type Tab = 'remarks' | 'inspections' | 'templates' | 'permits' | 'briefings' | 'credentials' | 'observations';
type Search = { tab?: Tab; project?: string; status?: string; source?: string };

const plusDays = (n: number) => todayLocal(new Date(Date.now() + n * 86_400_000));

const TAB_TITLES: Record<Tab, string> = {
  remarks: 'Замечания и предписания',
  inspections: 'Приёмки по чек-листам',
  templates: 'Шаблоны чек-листов',
  permits: 'Наряды-допуски',
  briefings: 'Инструктажи',
  credentials: 'Допуски работников',
  observations: 'Наблюдения за безопасностью',
};

const SOURCE_FILTER: Array<[RemarkSource, string]> = [
  ['otk', 'ОТК'],
  ['ohs', 'Охрана труда'],
  ['fire_safety', 'Пожарная безопасность'],
  ['ecology', 'Экология'],
  ['builders', 'Строители'],
];

export function QualityPage() {
  const search = useSearch({ strict: false }) as Search;
  const navigate = useNavigate();
  const tab: Tab = search.tab ?? 'remarks';
  const setSearch = (patch: Partial<Search>) =>
    navigate({ to: '/qhse', search: { ...search, ...patch } as never, replace: true });
  const staff = useMe().tenant?.staff ?? false;
  const issuers = useProjectsWith('supervision.issue');
  const starters = useProjectsWith('qhse.edit');
  const canStart = starters.list.some((p) => issuers.list.some((i) => i.id === p.id));
  const canEditTemplates = useCompanyWide('qhse.edit');
  const [newRemark, setNewRemark] = useState(false);
  const [newInspection, setNewInspection] = useState(false);
  useCrumbs(tab === 'remarks' ? null : [{ label: TAB_TITLES[tab] }]);
  const filter = (status: string | undefined) => (status === 'open' || !status ? undefined : status);
  return (
    <Page>
      <PageHeader
        title="Качество и ОТ"
        subtitle="Замечания и предписания, приёмки по чек-листам, наряды-допуски, инструктажи, допуски работников и наблюдения за безопасностью."
        actions={
          tab === 'remarks' && issuers.list.length ? (
            <Button
              variant="primary"
              size="lg"
              icon={<Plus className="size-4" />}
              onClick={() => setNewRemark(true)}
            >
              Новое замечание
            </Button>
          ) : tab === 'inspections' && canStart ? (
            <Button
              variant="primary"
              size="lg"
              icon={<Plus className="size-4" />}
              onClick={() => setNewInspection(true)}
            >
              Новая приёмка
            </Button>
          ) : tab === 'templates' && canEditTemplates ? (
            <Link
              to="/qhse/templates/$templateId"
              params={{ templateId: 'new' }}
              className="inline-flex h-12 items-center gap-2 rounded-md bg-accent px-5 text-[15px] font-semibold text-on-accent hover:underline"
            >
              <Plus className="size-4" />
              Новый шаблон
            </Link>
          ) : null
        }
      />
      <Tabs
        value={tab}
        onChange={(v) => setSearch({ tab: v === 'remarks' ? undefined : v, status: undefined })}
        items={[
          { value: 'remarks', label: 'Замечания и предписания' },
          ...(staff
            ? [
                { value: 'inspections' as const, label: 'Приёмки по чек-листам' },
                { value: 'templates' as const, label: 'Шаблоны чек-листов' },
                { value: 'permits' as const, label: 'Наряды-допуски' },
                { value: 'briefings' as const, label: 'Инструктажи' },
                { value: 'credentials' as const, label: 'Допуски работников' },
                { value: 'observations' as const, label: 'Наблюдения' },
              ]
            : []),
        ]}
      />
      {tab !== 'templates' ? (
        <div className="mb-4 flex flex-col gap-2 md:flex-row md:flex-wrap">
          <ProjectFilter
            value={search.project}
            onChange={(v) => setSearch({ project: v })}
            perms={tab === 'remarks' ? ['supervision.view', 'supervision.respond'] : ['qhse.view']}
          />
          {tab !== 'credentials' ? (
            <Select
              aria-label="Состояние"
              className="h-11 md:h-10 md:w-48"
              value={search.status ?? 'open'}
              onChange={(e) => setSearch({ status: e.target.value === 'open' ? undefined : e.target.value })}
            >
              <option value="open">Открытые</option>
              <option value="overdue">Просроченные</option>
              <option value="closed">Закрытые</option>
              <option value="all">Все</option>
            </Select>
          ) : null}
          {tab === 'remarks' ? (
            <Select
              aria-label="Источник"
              className="h-11 md:h-10 md:w-56"
              value={search.source ?? ''}
              onChange={(e) => setSearch({ source: e.target.value || undefined })}
            >
              <option value="">Все источники</option>
              {SOURCE_FILTER.map(([k, v]) => (
                <option key={k} value={k}>
                  {v}
                </option>
              ))}
            </Select>
          ) : null}
        </div>
      ) : null}
      {tab === 'remarks' ? (
        <RemarksList
          search={{ project: search.project, status: filter(search.status), source: search.source }}
        />
      ) : null}
      {tab === 'inspections' ? <InspectionsList search={search} /> : null}
      {tab === 'templates' ? <TemplatesList /> : null}
      {tab === 'permits' ? <PermitsList search={search} /> : null}
      {tab === 'briefings' ? <BriefingsList search={search} /> : null}
      {tab === 'credentials' ? <CredentialsList search={search} /> : null}
      {tab === 'observations' ? <ObservationsList search={search} /> : null}
      {newRemark ? <NewRemarkDialog projectId={search.project} onClose={() => setNewRemark(false)} /> : null}
      {newInspection ? (
        <NewInspectionDialog projectId={search.project} onClose={() => setNewInspection(false)} />
      ) : null}
    </Page>
  );
}

/* ───────────────────────── Приёмки ───────────────────────── */

export function InspectionBadge({
  i,
}: {
  i: Pick<ChecklistInspectionSummary, 'status' | 'result' | 'overdue'>;
}) {
  if (i.status === 'completed')
    return (
      <Badge tone={i.result === 'with_remarks' ? 'warning' : 'success'}>
        {i.result === 'with_remarks' ? 'Завершена, есть замечания' : 'Завершена'}
      </Badge>
    );
  if (i.status === 'cancelled') return <Badge>{CHECKLIST_STATUSES.cancelled}</Badge>;
  return i.overdue ? (
    <Badge tone="danger">Просрочена</Badge>
  ) : (
    <Badge tone="info">{CHECKLIST_STATUSES.in_progress}</Badge>
  );
}

function InspectionsList({ search }: { search: Search }) {
  const q = useQuery({
    queryKey: ['qa-inspections', search.project, search.status],
    queryFn: () =>
      api<ChecklistInspectionSummary[]>('/quality/inspections', {
        query: {
          projectId: search.project,
          status: (search.status === 'closed' ? 'completed' : (search.status ?? 'open')) as string,
        },
      }),
  });
  if (q.isPending) return <Spinner />;
  if (q.isError) return <ErrorBox error={q.error} />;
  if (!q.data.length)
    return (
      <EmptyState title="Приёмок нет">
        Приёмку по чек-листу начинает ОТК или ответственный за качество: выберите шаблон, место и исполнителя.
      </EmptyState>
    );
  return (
    <ul className="flex flex-col gap-2">
      {q.data.map((x) => (
        <li key={x.id}>
          <Link
            to="/qhse/inspections/$inspectionId"
            params={{ inspectionId: x.id }}
            className="flex flex-col gap-1.5 rounded-lg border border-line bg-surface p-4 hover:border-ink-3 md:flex-row md:items-start md:gap-4"
          >
            <span className="num shrink-0 font-medium text-ink md:w-28">{x.number}</span>
            <span className="flex min-w-0 flex-1 flex-col gap-0.5">
              <span className="text-ink [overflow-wrap:anywhere]">{x.title}</span>
              <span className="text-[13px] text-ink-2 [overflow-wrap:anywhere]">
                {x.projectCode}
                {x.objectName ? ` · ${x.objectName}` : ''}
                {x.place ? ` · ${x.place}` : ''} · исполнитель {x.responsibleName} · пунктов {x.answered} из{' '}
                {x.total}
                {x.failed ? `, не соответствует: ${x.failed}` : ''}
              </span>
            </span>
            <span className="flex flex-wrap items-center gap-1.5 md:justify-end">
              <InspectionBadge i={x} />
              <span className="text-[13px] text-ink-2">до {date(x.dueDate)}</span>
            </span>
          </Link>
        </li>
      ))}
    </ul>
  );
}

type People = Array<{ id: string; fullName: string; external: boolean }>;

export function NewInspectionDialog({ projectId, onClose }: { projectId?: string; onClose: () => void }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const starters = useProjectsWith('qhse.edit');
  const issuers = useProjectsWith('supervision.issue');
  const list = starters.list.filter((p) => issuers.list.some((i) => i.id === p.id));
  const [f, setF] = useState({
    projectId: projectId && list.some((p) => p.id === projectId) ? projectId : (list[0]?.id ?? ''),
    templateId: '',
    objectId: '',
    place: '',
    contractorId: '',
    responsibleId: '',
    remarkAssigneeId: '',
    dueDate: plusDays(3),
  });
  const setProject = useCallback((id: string) => setF((x) => ({ ...x, projectId: id })), []);
  useProjectDefault(list, projectId, f.projectId, setProject);
  const set = <K extends keyof typeof f>(k: K, v: (typeof f)[K]) => setF((x) => ({ ...x, [k]: v }));
  const templates = useQuery({
    queryKey: ['qa-templates', 'active'],
    queryFn: () =>
      api<ChecklistTemplateSummary[]>('/quality/checklist-templates', { query: { status: 'active' } }),
  });
  const project = useQuery({ ...projectQuery(f.projectId), enabled: Boolean(f.projectId) });
  const cps = useQuery({ ...counterpartiesQuery('contractor'), retry: false });
  const responsible = useQuery({
    queryKey: ['qa-people', f.projectId, 'responsible'],
    queryFn: () => api<People>('/quality/people', { query: { projectId: f.projectId, role: 'responsible' } }),
    enabled: Boolean(f.projectId),
  });
  const assignees = useQuery({
    queryKey: ['qa-people', f.projectId, 'assignee'],
    queryFn: () => api<People>('/quality/people', { query: { projectId: f.projectId, role: 'assignee' } }),
    enabled: Boolean(f.projectId),
  });
  const m = useMutation({
    mutationFn: () =>
      api<ChecklistInspectionDetail>('/quality/inspections', {
        body: {
          projectId: f.projectId,
          templateId: f.templateId,
          objectId: f.objectId || null,
          place: f.place.trim() || null,
          contractorId: f.contractorId || null,
          responsibleId: f.responsibleId,
          remarkAssigneeId: f.remarkAssigneeId || null,
          dueDate: f.dueDate,
        },
      }),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ['qa-inspections'] });
      qc.invalidateQueries({ queryKey: ['tasks'] });
      qc.invalidateQueries({ queryKey: ['inbox'] });
      toast(`Приёмка ${r.number} создана`);
      onClose();
      navigate({ to: '/qhse/inspections/$inspectionId', params: { inspectionId: r.id } });
    },
  });
  return (
    <Dialog
      open
      width="lg"
      onOpenChange={(v) => !v && onClose()}
      title="Новая приёмка по чек-листу"
      description="Пункты берутся из шаблона на сегодня: позже правка шаблона эту приёмку не изменит."
      footer={
        <>
          <Button variant="ghost" className="h-11 md:h-10" onClick={onClose}>
            Отмена
          </Button>
          <Button
            variant="primary"
            className="h-11 md:h-10"
            loading={m.isPending}
            disabled={!f.projectId || !f.templateId || !f.responsibleId || !f.dueDate}
            onClick={() => m.mutate()}
          >
            Создать
          </Button>
        </>
      }
    >
      {m.isError ? <ErrorBox error={m.error} className="mb-3" /> : null}
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Проект" className="sm:col-span-2">
          <Select
            className="h-11 md:h-10"
            value={f.projectId}
            onChange={(e) =>
              setF((x) => ({
                ...x,
                projectId: e.target.value,
                objectId: '',
                responsibleId: '',
                remarkAssigneeId: '',
              }))
            }
          >
            {list.map((p) => (
              <option key={p.id} value={p.id}>
                {p.code} {p.name}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Шаблон чек-листа" className="sm:col-span-2">
          <Select
            className="h-11 md:h-10"
            value={f.templateId}
            onChange={(e) => set('templateId', e.target.value)}
          >
            <option value="">Выберите шаблон</option>
            {(templates.data ?? []).map((t) => (
              <option key={t.id} value={t.id}>
                {t.title} ({CHECKLIST_WORK_KINDS[t.workKind]}, пунктов {t.itemCount})
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Объект">
          <Select
            className="h-11 md:h-10"
            value={f.objectId}
            onChange={(e) => set('objectId', e.target.value)}
          >
            <option value="">—</option>
            {(project.data?.objects ?? []).map((o) => (
              <option key={o.id} value={o.id}>
                {o.code ? `${o.code} ` : ''}
                {o.name}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Место" hint="Оси, отметки, захватка">
          <Input className="h-11 md:h-10" value={f.place} onChange={(e) => set('place', e.target.value)} />
        </Field>
        <Field label="Кто заполняет">
          <Select
            className="h-11 md:h-10"
            value={f.responsibleId}
            onChange={(e) => set('responsibleId', e.target.value)}
          >
            <option value="">Выберите сотрудника</option>
            {(responsible.data ?? []).map((p) => (
              <option key={p.id} value={p.id}>
                {p.fullName}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Срок приёмки">
          <Input
            className="h-11 md:h-10"
            type="date"
            value={f.dueDate}
            onChange={(e) => set('dueDate', e.target.value)}
          />
        </Field>
        <Field label="Подрядчик" hint="Замечания по приёмке будут адресованы ему и станут видны подрядчику">
          <Select
            className="h-11 md:h-10"
            value={f.contractorId}
            onChange={(e) => set('contractorId', e.target.value)}
          >
            <option value="">Без адресата (внутренние)</option>
            {(cps.data ?? []).map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Кто устраняет замечания">
          <Select
            className="h-11 md:h-10"
            value={f.remarkAssigneeId}
            onChange={(e) => set('remarkAssigneeId', e.target.value)}
          >
            <option value="">Не назначен</option>
            {(assignees.data ?? []).map((p) => (
              <option key={p.id} value={p.id}>
                {p.fullName}
                {p.external ? ' (внешний)' : ''}
              </option>
            ))}
          </Select>
        </Field>
      </div>
    </Dialog>
  );
}

/* ───────────────────────── Шаблоны ───────────────────────── */

function TemplatesList() {
  const [all, setAll] = useState(false);
  const q = useQuery({
    queryKey: ['qa-templates', all ? 'all' : 'active'],
    queryFn: () =>
      api<ChecklistTemplateSummary[]>('/quality/checklist-templates', {
        query: { status: all ? 'all' : 'active' },
      }),
  });
  return (
    <>
      <label className="mb-3 flex min-h-11 items-center gap-2 text-[14px] text-ink">
        <input type="checkbox" checked={all} onChange={(e) => setAll(e.target.checked)} className="size-4" />
        Показать и архивные
      </label>
      {q.isPending ? (
        <Spinner />
      ) : q.isError ? (
        <ErrorBox error={q.error} />
      ) : !q.data.length ? (
        <EmptyState title="Шаблонов нет">
          Шаблон описывает пункты проверки: армирование, опалубка, бетонирование, сварка, изоляция, обратная
          засыпка. Правит шаблоны тот, кто ведёт проверки по всей компании.
        </EmptyState>
      ) : (
        <ul className="flex flex-col gap-2">
          {q.data.map((t) => (
            <li key={t.id}>
              <Link
                to="/qhse/templates/$templateId"
                params={{ templateId: t.id }}
                className="flex flex-col gap-1.5 rounded-lg border border-line bg-surface p-4 hover:border-ink-3 md:flex-row md:items-center md:gap-4"
              >
                <span className="min-w-0 flex-1 text-ink [overflow-wrap:anywhere]">{t.title}</span>
                <span className="flex flex-wrap items-center gap-1.5 text-[13px] text-ink-2">
                  <Badge>{CHECKLIST_WORK_KINDS[t.workKind]}</Badge>
                  <span>пунктов {t.itemCount}</span>
                  <span>версия {t.revision}</span>
                  {t.status === 'archived' ? <Badge tone="warning">В архиве</Badge> : null}
                </span>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </>
  );
}
